import {
    AfterViewInit, Component, ElementRef, OnDestroy, ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, firstValueFrom, startWith, tap } from 'rxjs';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ConnectionService } from 'src/app/services/connection.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { ChatResponse, ChatSource, RagService } from 'src/app/services/rag.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ApiService } from 'src/app/services/api.service';
import {
    ApolloService, getChat_chats_by_pk_Messages, getCollections_collections,
    getMessageAttachmentsByPk_message_attachments_by_pk,
} from 'src/gql-generated/generated';
import { Location, NgIf, NgClass, NgFor, NgTemplateOutlet, DatePipe } from '@angular/common';
import { DynamicDialogComponent } from '../shared/dynamic-dialog/dynamic-dialog.component';
import { FileSelectorComponent, UploadedFileType } from '../shared/file-selector/file-selector.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MarkdownComponent } from 'ngx-markdown';
import { ShimmerDirective } from '../../directives/shimmer.directive';
import { PulseDirective } from '../../directives/pulse.directive';
import { ExpansionContentComponent } from '../shared/expansion-content/expansion-content.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatCheckbox } from '@angular/material/checkbox';
import { CanViewFileInBrowserPipe } from '../../pipes/can-view-file-in-browser.pipe';
import { ChatFeedService } from 'src/app/services/chat-feed.service';

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrl: './chat.component.scss',
    imports: [
        NgIf,
        NgClass,
        MatDrawerContainer,
        MatDrawerContent,
        MatIconButton,
        MatIcon,
        RouterLink,
        NgFor,
        MatTooltip,
        MarkdownComponent,
        ShimmerDirective,
        PulseDirective,
        ExpansionContentComponent,
        MatButton,
        MatProgressSpinner,
        MatFormField,
        MatInput,
        CdkTextareaAutosize,
        ReactiveFormsModule,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        FileSelectorComponent,
        MatCheckbox,
        MatDrawer,
        DynamicDialogComponent,
        NgTemplateOutlet,
        DatePipe,
        CanViewFileInBrowserPipe,
    ],
})
export class ChatComponent implements OnDestroy, AfterViewInit {
    /**
     * Reference to messages container. Used to scroll to certain parts
     */
    @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

    /**
     * Reference to drawer
     */
    @ViewChild(MatDrawer) drawer?: MatDrawer;

    /**
     * Reference to hidden file selector for uploading in-chat documents
     */
    @ViewChild('fileSelector') fileSelector?: FileSelectorComponent;

    /**
     * Reference to dialog that showcases a detailed message_attachment object
     */
    @ViewChild('attachmentDialog') attachmentDialog?: DynamicDialogComponent;

    /**
     * Control tracking currently selected collection
     */
    selectedCollection?: getCollections_collections;

    /**
     * Control tracking input in chat box
     */
    chatControl = new FormControl<string>('', { validators: [Validators.required], nonNullable: true });

    /**
     * Messages (request and responses) to render
     */
    messages: Message[] = [];

    /**
     * True if sources should be displayed besides chat content
     */
    showSources = false;

    /**
     * True if user should be blocked from sending more promts (e.g. during previous promt response).
     */
    blockSend = false;

    /**
     * Sources user wants to see in sidenav
     */
    activeSources: ChatResponse['sources'] = [];

    /**
     * Default height for responses. Based on the height of messagesContainer via the resizeObserver
     */
    defaultResponseHeight = 500;

    /**
     * True when component setup is completed and content should be shown
     */
    setupCompleted = false;

    /**
     * Set to true if the chatbot should be forced to search chunks/sources for the result
     */
    retrieveChunks = true;

    /**
     * True after first response is completed. Allows the user to change the value of retrieveChunks via a button.
     */
    showChunkRetrievalButton = false;

    /**
     * Set to true to show that current collection is still processing
     */
    showProcessingWarning = false;

    /**
     * Set to true to show that current collection has failed documents
     */
    showFailedDocumentsWarning = false;

    /**
     * Set to true to show that current collection has been deleted
     */
    showCollectionDeletedWarning = false;

    /**
     * Attachment to show in the attachment detail dialog
     */
    attachmentDetail?: getMessageAttachmentsByPk_message_attachments_by_pk;

    /**
     * Subtitle of attachment detail dialog
     * Stored here for the $localize functionality
     */
    attachmentDetailSubtitle = $localize`Opmaak kan afwijken van het origineel`;

    /**
     * Subtitle of chunk detail dialog
     * Stored here for the $localize functionality
     */
    chunkDetailSubtitle = $localize`Pagina: `;

    /**
     * Resize observer to track changes in height of messagesContainer
     */
    private resizeObserver?: ResizeObserver;

    /**
     * Placeholder for the chat prompt input text area
     */
    chatInputPlaceholder = $localize`Stel je vraag...`;

    constructor(
        private apollo: ApolloService,
        private rag: RagService,
        private snackbar: SnackbarService,
        private router: Router,
        private route: ActivatedRoute,
        public breakpoint: BreakpointService,
        public organization: OrganizationService,
        private sentry: SentryService,
        public connection: ConnectionService,
        private location: Location,
        public chatFeedService: ChatFeedService,
        private api: ApiService,
    ) {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
            takeUntilDestroyed(),
        ).subscribe(() => {
            this.resetState();
            // Get chatId from url
            const selectedChatId = this.route.snapshot.paramMap.get('chatId') || undefined;
            this.chatFeedService.selectedChatId$.next(selectedChatId);
            const collectionIdQueryParam = this.route.snapshot.queryParamMap.get('collection') || undefined;
            // If there is a chatId we will preload the chat
            if (selectedChatId && selectedChatId !== 'new') {
                firstValueFrom(this.apollo.getChat({ id: selectedChatId }))
                    .then((response) => {
                        // To visit historic chats the title must be defined
                        if (!response.data?.chats_by_pk?.title) {
                            this.router.navigate(['chat', 'new']);
                            return;
                        }
                        this.messages = [];
                        const collection = response.data.chats_by_pk.Collection;
                        this.selectedCollection = collection ?? undefined;

                        // Load all messages
                        this.fillMessages(response.data.chats_by_pk.Messages);
                        // RAG specific settings only required when chatting in a collection
                        if (this.selectedCollection) {
                            // Show warnings
                            this.showProcessingWarning = collection?.status === 'processing';
                            this.showFailedDocumentsWarning = collection?.status === 'failed';
                            this.showCollectionDeletedWarning = !!collection?.deleted_at;
                            // Show chunk retrieval button if feature is enabled in organization
                            if (this.organization.currentOrganization$.value?.settings?.features.includes('ChunkRetrievalButton')) {
                                this.retrieveChunks = true;
                                this.showChunkRetrievalButton = true;
                            }
                        }
                        // Show component content
                        this.setupCompleted = true;
                    })
                    // Redirect to new collection on apollo error
                    .catch(() => this.router.navigate(['chat', 'new'], { replaceUrl: true }));
            } else if (collectionIdQueryParam) {
                // Collection has been selected with query params
                firstValueFrom(this.apollo.getCollection({ id: collectionIdQueryParam }))
                    .then((response) => {
                        this.messages = [];
                        const collection = response.data?.collections_by_pk;
                        this.selectedCollection = collection ?? undefined;

                        // Show warnings
                        this.showProcessingWarning = collection?.status === 'processing';
                        this.showFailedDocumentsWarning = collection?.status === 'failed';
                        this.showCollectionDeletedWarning = !!collection?.deleted_at;
                        // Show component content
                        this.setupCompleted = true;
                    })
                    // Redirect to new collection on apollo error
                    .catch(() => this.router.navigate(['chat', 'new'], { replaceUrl: true }));
            } else if (!this.organization.currentOrganization$.value?.settings?.features.includes('BaseModelChat')) {
                // User is trying to start new chat with base model - check if feature flag is enabled, if not redirect
                this.router.navigate(['chat', 'new']);
            } else {
                // Starting new chat with base model
                this.setupCompleted = true;
            }
        });
        setTimeout(() => {
            this.trackHeightChanges();
        });
    }

    /**
     * Logic after view is initialized
     */
    ngAfterViewInit(): void {
        // Subscribe to selected files - emits when new files are selected
        this.fileSelector?.filesSelected.subscribe((fileArray) => {
            this.handleFilesSelectedUpdate(fileArray);
        });
    }

    /**
     * Determines if there is room to show content in the center of the screen
     * Used for showcasing the "Chatten zonder collectie" and prompt input field in the center of the screen
     * If there is a selected collection or this is a historic chat (there are messages) return false
     * @returns true if there is room for content in the center, false otherwise
     */
    canShowContentInCenter(): boolean {
        if (this.messages.length > 0) {
            return false;
        }
        return !this.route.snapshot.queryParamMap.get('collection') && !this.route.snapshot.paramMap.get('chatId');
    }

    /**
     * Determine if the plus button should be shown in the chat input box based on if
     * BaseModelChat feature is enabled and if selectedCollection is defined
     * @returns true if the plus button should be shown (base chats) false if it shouldn't (collection chats)
     */
    showPlusButton(): boolean {
        if (!this.organization.currentOrganization$.value?.settings?.features.includes('BaseModelChat')) {
            return false;
        }
        return !!this.selectedCollection;
    }

    /**
     * Update this.retrieveChunks to its negating value
     */
    toggleRetrieveChunks(): void {
        this.retrieveChunks = !this.retrieveChunks;
    }

    /**
     * Handle newly selected files by uploading them to the backend
     * If successful set the file's attachment_id, id and status to available
     * If not successful set the file's status to failed
     * @param fileArray array of files which are all currently selected files
     */
    async handleFilesSelectedUpdate(fileArray: UploadedFileType[]): Promise<void> {
        const newFiles = fileArray.filter((entry) => entry.status === 'processing');
        if (newFiles.length > 0) {
            this.blockSend = true;
            // Construct backend upload + attachment process Promise for each new file
            const promises: Promise<{ id: string | null; file_id: string | null; }>[] = newFiles.map(async (newFile) => {
                try {
                    const uploadResponse = await this.api.uploadFile(newFile.file);
                    // Set organization_id for file
                    await firstValueFrom(this.apollo.updateFiles({
                        fileIds: [uploadResponse.data.file_id],
                        fileUpdates: { organization_id: this.organization.currentOrganization$.value?.id },
                    }));
                    const attachmentProcessResponse = await this.rag.attachmentProcess(uploadResponse.data.file_id);
                    newFile.attachment_id = attachmentProcessResponse.id;
                    newFile.id = attachmentProcessResponse.file_id;
                    newFile.status = 'available';
                    return { id: attachmentProcessResponse.id, file_id: attachmentProcessResponse.file_id };
                } catch {
                    this.snackbar.error($localize`Kan bestand niet uploaden`);
                    newFile.status = 'failed';
                    return { id: null, file_id: null };
                }
            });

            await Promise.all(promises);
        }

        // If all files are now available allow sending of prompt
        if (this.fileSelector?.files.every((entry) => entry.status === 'available')) {
            this.blockSend = false;
        }
    }

    /**
     * Reset the state of the chat.component by setting attributes to their initial values
     */
    resetState(): void {
        this.messages = [];
        this.showProcessingWarning = false;
        this.showFailedDocumentsWarning = false;
        this.showChunkRetrievalButton = false;
        this.showCollectionDeletedWarning = false;
        this.blockSend = false;
        this.drawer?.close();
    }

    /**
     * Fill the this.messages frontend array with messages from hasura
     * @param messages messages from hasura to insert into the this.messages array
     */
    private fillMessages(messages: getChat_chats_by_pk_Messages[]): void {
        // Position refers to the message's position in the conversation
        messages.sort((a, b) => (a.position - b.position));
        for (const message of messages) {
            if (message.role === 'assistant') {
                this.messages.push({
                    type: 'response',
                    content: message.content,
                    reasoningContent: message.reasoning_content ?? undefined,
                    sources: message.Sources.map((source) => ({
                        id: source.Chunk.id,
                        text: source.Chunk.text,
                        pages: source.Chunk.pages ?? undefined,
                        document: {
                            id: source.Chunk.Document!.id,
                            name: source.Chunk.Document!.File.name,
                            file_id: source.Chunk.Document!.File.id,
                            deleted_at: source.Chunk.Document?.deleted_at ?? null,
                        },
                        cosine_similarity: source.cosine_similarity,
                        reranker_score: source.reranker_score,
                    })),
                });
            } else if (message.role === 'user') {
                this.messages.push({
                    type: 'prompt',
                    content: message.content,
                    attachments: message.Attachments.map((attachment) => ({
                        id: attachment.id,
                        fileId: attachment.File.id,
                        fileName: attachment.File.name,
                    })),
                });
            }
        }
    }

    /**
     * Track changes in height of messagesContainer after view inits to set defaultResponseHeight
     */
    trackHeightChanges(): void {
        if (this.messagesContainer && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.messagesContainer) {
                    this.defaultResponseHeight = this.messagesContainer.nativeElement.clientHeight;
                }
            });

            this.resizeObserver.observe(this.messagesContainer.nativeElement);
        }
    }

    /**
     * Send the user input prompt as a chat message to the backend
     * @param event Optional keydown enter event
     */
    async sendMessage(event?: Event): Promise<void> {
        if (this.chatControl.valid && !this.blockSend) {
            const prompt = this.chatControl.value;
            this.chatControl.reset();

            // Prevent default of the enter keydown event so we don't create an unnecessary new line character
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            // Define uploaded message attachments that should be added to the prompt
            const messageAttachments = this.fileSelector?.files.filter((entry) => entry.id && entry.attachment_id && entry.status === 'available');

            // Add prompt and placeholder response to messages
            this.messages.push(
                {
                    type: 'prompt',
                    content: prompt,
                    ...(messageAttachments && {
                        attachments: messageAttachments.map((attachment) => (
                            { id: attachment.attachment_id!, fileId: attachment.id!, fileName: attachment.file.name }
                        )),
                    }),
                },
            );
            this.messages.push({ type: 'response', content: '', reasoningContent: '' });

            // Empty file selector so that they don't appear in the chat input box
            if (this.fileSelector) {
                this.fileSelector.files = [];
            }

            // Scroll latest prompt to top
            setTimeout(() => {
                if (this.messagesContainer) {
                    // Latest prompt is second to last child of second child of messagesContainer
                    const container = this.messagesContainer.nativeElement as HTMLElement;
                    const elements = this.selectedCollection ? container.children[1].children : container.children[0].children;
                    const secondToLastElement = elements[elements.length - 2] as HTMLElement;
                    container.scrollTo({
                        top: secondToLastElement.offsetTop - container.offsetTop - 16,
                        behavior: 'smooth',
                    });
                }
            });

            // Send prompt to AI and handle response
            await this.rag.chat(
                prompt,
                this.selectedCollection?.id,
                this.chatFeedService.selectedChatId$.value,
                messageAttachments?.map((attachment) => attachment.attachment_id!),
                this.retrieveChunks,
            )
                .then((observable) => {
                    // Clear prompt input and prevent the user from sending more messages
                    this.chatControl.reset();
                    this.blockSend = true;

                    // Placeholder for reasoning start timestamp
                    let reasoningStartTimestamp: number | undefined;

                    // Set retrieve chunks to true for next prompt
                    this.retrieveChunks = true;

                    // Handle content
                    const response = this.messages.at(-1);
                    observable.pipe(
                        tap(data => {
                            if (data.type === 'error') {
                                throw new Error('Streaming error');
                            }
                        }),
                    ).subscribe({
                        next: (data) => {
                            // Update content
                            if (response) {
                                if (this.chatFeedService.selectedChatId$.value &&
                                    data.id &&
                                    data.id !== this.chatFeedService.selectedChatId$.value) {
                                    return;
                                }

                                response.content += data.content || '';
                                response.reasoningContent += data.reasoning_content || '';

                                // Start tracking reasoning on first reasoning message
                                if (!reasoningStartTimestamp && response.reasoningContent) {
                                    reasoningStartTimestamp = new Date().getTime();
                                    response.isReasoning = true;
                                }

                                // Set reasoning to be complete on first normal content block
                                if (response.isReasoning && response.content) {
                                    if (reasoningStartTimestamp) {
                                        response.reasoningDuration = Math.ceil((new Date().getTime() - reasoningStartTimestamp) / 1000);
                                    }
                                    response.isReasoning = false;
                                }

                                // Set sources
                                if (data.sources) {
                                    response.sources = data.sources;
                                }
                            }
                            if (!this.chatFeedService.selectedChatId$.value && data.type === 'chatendsources') {
                                // Last message will contain chat id
                                this.chatFeedService.selectedChatId$.next(data.id);
                            }
                        },
                        complete: () => {
                            // Update url if this chat is new
                            if (!this.route.snapshot.paramMap.get('chatId')) {
                                this.location.replaceState(`/chat/${this.chatFeedService.selectedChatId$.value}`);
                            }

                            // Allow sending new prompts after previous completed
                            this.blockSend = false;

                            // Show chunk retrieval button if feature is enabled in organization and we're in RAG mode
                            if (this.organization.currentOrganization$.value?.settings?.features.includes('ChunkRetrievalButton') &&
                                this.selectedCollection) {
                                this.showChunkRetrievalButton = true;
                            }
                        },
                        error: () => {
                            this.snackbar.error($localize`Fout tijdens het genereren van antwoord`);
                            if (response) {
                                response.isReasoning = false;
                            }
                        },
                    });
                }).catch((error: unknown) => {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Fout tijdens het genereren van antwoord`);
                    if (this.messages.at(-1)?.type === 'response') {
                        this.messages.at(-1)!.hideWaitAnimation = true;
                    }
                });
        }
    }

    /**
     * Set the sources in the sidebar and open drawer
     * @param sources Sources to display in sidebar
     * @param drawer Drawer to open
     */
    setSources(sources: Message['sources'], drawer: MatDrawer): void {
        this.activeSources = sources;
        drawer.open();
    }

    /**
     * Open the file of a source in a new tab, optionally at the pages associated with a chunk
     * @param file_id ID of file to download from backend and open in-browser
     * @param chunk_id (optional) ID of the chunk to fetch pages from
     */
    async viewSourceFile(file_id: string, chunk_id?: ChatSource['id']): Promise<void> {
        try {
            let pages: number[] | null = null;
            // Retrieve pages if chunk_id is provided
            if (chunk_id) {
                const chunkResult = await firstValueFrom(
                    this.apollo.getChunk({ id: chunk_id }),
                );
                // Get the pages array, default to null if not set
                pages = chunkResult.data?.chunks_by_pk?.pages ?? null;
            }

            const blob = (await this.api.retrieveFile(file_id)).data;

            // Open file in new browser tab and scroll to correct page if it is defined
            const fileUrl = URL.createObjectURL(blob);
            const urlToOpen = pages != null ? `${fileUrl}#page=${pages[0]}` : fileUrl;
            window.open(urlToOpen);
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Bestand openen mislukt`);
        }
    }

    /**
     * Handle download button press
     * @param file File that was pressed
     */
    async downloadSourceFile(fileId: string, fileName: string): Promise<void> {
        try {
            await this.api.downloadFile(
                fileId,
                fileName,
            );
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Bestand downloaden mislukt`);
        }
    }

    /**
     * Open the chunk dialog to display chunk content
     * @param chunkDialog Dialog component to open
     */
    showSource(chunkDialog: DynamicDialogComponent): void {
        try {
            chunkDialog.open();
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Bron openen mislukt`);
        }
    }

    /**
     * Open the system's file browser to upload files by clicking on the hidden input file element
     */
    uploadFiles(): void {
        this.fileSelector?.fileUpload?.nativeElement.click();
    }

    /**
     * Remove an in-chat uploaded file from the list, minio and files table (with backend call) and from the message_attachments table
     * @param file file to remove
     */
    async removeFile(file: UploadedFileType): Promise<void> {
        if (this.fileSelector) {
            // Files that should remain after deletion
            const remainingFiles = this.fileSelector.files.filter((entry) => entry.file !== file.file);

            // Files that should be deleted
            const deletedFiles = this.fileSelector.files.filter((entry) => entry.file === file.file);

            // Delete files from minio and files table (backend call) and message_attachments table
            await Promise.allSettled(
                deletedFiles.map(async (deletedFile) => {
                    if (deletedFile.id) {
                        await this.api.deleteFile(deletedFile.id);
                    }
                    if (deletedFile.attachment_id) {
                        await firstValueFrom(this.apollo.deleteMessageAttachmentByPk({ id: deletedFile.attachment_id }));
                    }
                }),
            );

            // Files shown in overview
            this.fileSelector.files = remainingFiles;

            // If all files are now available allow sending of prompt
            if (this.fileSelector.files.every((entry) => entry.status === 'available')) {
                this.blockSend = false;
            }
        }
    }

    /**
     * Open the message attachment dialog which shows the parsed content of a message_attachment in detail
     * First retrieves the message_attachment, then opens the dialog
     * @param attachmentId id of message_attachment to showcase
     */
    async openAttachmentDialog(attachmentId: string | undefined): Promise<void> {
        if (attachmentId === undefined) {
            return;
        }
        // Retrieve full message_attachment
        const attachmentResult = await firstValueFrom(
            this.apollo.getMessageAttachmentsByPk({ id: attachmentId }),
        );
        if (!attachmentResult.data?.message_attachments_by_pk) {
            return;
        }
        this.attachmentDetail = attachmentResult.data.message_attachments_by_pk;
        this.attachmentDialog?.open();
    }

    /**
     * Triggered when the component is destroyed
     * Sets the selectedChatId in the chatFeedService to undefined
     */
    ngOnDestroy(): void {
        this.chatFeedService.selectedChatId$.next(undefined);
    }
}

interface Message {
    type: 'prompt' | 'response';
    content: string;
    reasoningDuration?: number;
    isReasoning?: boolean;
    reasoningContent?: string;
    sources?: ChatResponse['sources'];
    hideWaitAnimation?: boolean;
    attachments?: MessageAttachment[];
}

export interface MessageAttachment {
    id: string;
    fileId: string;
    fileName: string;
}
